import React, { useEffect, useState } from 'react';
import { mockReimbursements } from '../mockData';
import { useAuth } from '../context/AuthContext';
import PayrollAdmin from './PayrollAdmin';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Download } from 'lucide-react';

const createSalarySlipPdf = (employeeName, month, year, netAmount) => {
  const escapePdfText = (value) => String(value).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');

  const stream = [
    'BT',
    '/F1 20 Tf',
    '72 740 Td',
    `(Salary Slip - ${escapePdfText(month)} ${escapePdfText(year)}) Tj`,
    '0 -34 Td',
    '/F1 12 Tf',
    `(Employee: ${escapePdfText(employeeName)}) Tj`,
    '0 -20 Td',
    `(Net Amount: INR ${escapePdfText(netAmount)}) Tj`,
    '0 -20 Td',
    '(This is a generated preview salary slip.) Tj',
    'ET'
  ].join('\n');

  const objects = [
    null,
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >>',
    `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>'
  ];

  let pdf = '%PDF-1.4\n';
  const offsets = [0];

  for (let index = 1; index < objects.length; index += 1) {
    offsets[index] = pdf.length;
    pdf += `${index} 0 obj\n${objects[index]}\nendobj\n`;
  }

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length}\n0000000000 65535 f \n`;
  for (let index = 1; index < objects.length; index += 1) {
    pdf += `${String(offsets[index]).padStart(10, '0')} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return new Blob([pdf], { type: 'application/pdf' });
};

const employeeSalaryRuns = [
  { id: 'PS-2026-01', month: 'January', year: '2026', gross: 120000, deductions: 21550, net: 98450 },
  { id: 'PS-2026-02', month: 'February', year: '2026', gross: 120000, deductions: 21900, net: 98100 },
  { id: 'PS-2026-03', month: 'March', year: '2026', gross: 120000, deductions: 21740, net: 98260 }
];

const EmployeePayrollView = ({ user }) => {
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [selectedSlipId, setSelectedSlipId] = useState(employeeSalaryRuns[0].id);
  const [employeeReimbursements, setEmployeeReimbursements] = useState(mockReimbursements);
  const [reimbursementForm, setReimbursementForm] = useState({
    type: 'Travel',
    amount: '',
    date: '',
    description: ''
  });
  const [slipUrl, setSlipUrl] = useState('');

  const salaryRuns = employeeSalaryRuns.filter((run) => {
    const byMonth = !monthFilter || run.month.toLowerCase() === monthFilter.toLowerCase();
    const byYear = !yearFilter || run.year === yearFilter;
    return byMonth && byYear;
  });

  const selectedSlip = salaryRuns.find((run) => run.id === selectedSlipId) || salaryRuns[0];

  useEffect(() => {
    if (!selectedSlip) {
      setSlipUrl('');
      return undefined;
    }
    const pdfBlob = createSalarySlipPdf(user?.name || 'Employee', selectedSlip.month, selectedSlip.year, selectedSlip.net.toLocaleString());
    const nextUrl = URL.createObjectURL(pdfBlob);
    setSlipUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [selectedSlip, user?.name]);

  const submitReimbursement = (event) => {
    event.preventDefault();
    if (!reimbursementForm.amount || !reimbursementForm.date) {
      return;
    }
    setEmployeeReimbursements((prev) => [
      {
        id: `REIMB-${Date.now()}`,
        employeeId: 'EMP001',
        employeeName: user?.name || 'Employee',
        type: reimbursementForm.type,
        amount: Number(reimbursementForm.amount),
        date: reimbursementForm.date,
        status: 'pending',
        description: reimbursementForm.description || 'New reimbursement request'
      },
      ...prev
    ]);
    setReimbursementForm({ type: 'Travel', amount: '', date: '', description: '' });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Finance</h1>
        <p className="text-gray-500 mt-1">Payroll and reimbursement for employee role</p>
      </div>

      <Tabs defaultValue="payroll">
        <TabsList>
          <TabsTrigger value="payroll">Payroll</TabsTrigger>
          <TabsTrigger value="reimbursement">Reimbursement</TabsTrigger>
        </TabsList>

        <TabsContent value="payroll" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Payroll Filters</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input placeholder="Month (e.g. March)" value={monthFilter} onChange={(event) => setMonthFilter(event.target.value)} />
              <Input placeholder="Year (e.g. 2026)" value={yearFilter} onChange={(event) => setYearFilter(event.target.value)} />
              <Button variant="outline" onClick={() => { setMonthFilter(''); setYearFilter(''); }}>
                Reset
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Salary Slip Result Grid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg mb-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead>Year</TableHead>
                      <TableHead>Gross</TableHead>
                      <TableHead>Deductions</TableHead>
                      <TableHead>Net</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salaryRuns.map((run) => (
                      <TableRow
                        key={run.id}
                        className={`cursor-pointer ${selectedSlip?.id === run.id ? 'bg-blue-50' : ''}`}
                        onClick={() => setSelectedSlipId(run.id)}
                      >
                        <TableCell>{run.month}</TableCell>
                        <TableCell>{run.year}</TableCell>
                        <TableCell>{run.gross.toLocaleString()}</TableCell>
                        <TableCell>{run.deductions.toLocaleString()}</TableCell>
                        <TableCell>{run.net.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="space-y-3">
                <div className="h-[420px] border rounded-lg overflow-hidden bg-white">
                  {slipUrl ? (
                    <iframe title="Salary Slip PDF" src={slipUrl} className="w-full h-full border-0" />
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-500">Select a salary slip to preview PDF</div>
                  )}
                </div>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={!slipUrl}
                  onClick={() => {
                    if (!slipUrl || !selectedSlip) return;
                    const link = document.createElement('a');
                    link.href = slipUrl;
                    link.download = `salary-slip-${selectedSlip.month}-${selectedSlip.year}.pdf`;
                    link.click();
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Salary Slip
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reimbursement" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Reimbursement</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitReimbursement} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <select
                  value={reimbursementForm.type}
                  onChange={(event) => setReimbursementForm((prev) => ({ ...prev, type: event.target.value }))}
                  className="h-10 rounded-md border border-gray-300 px-3 text-sm"
                >
                  <option value="Travel">Travel</option>
                  <option value="Food">Food</option>
                  <option value="Medical">Medical</option>
                </select>
                <Input
                  type="number"
                  placeholder="Amount"
                  value={reimbursementForm.amount}
                  onChange={(event) => setReimbursementForm((prev) => ({ ...prev, amount: event.target.value }))}
                />
                <Input
                  type="date"
                  value={reimbursementForm.date}
                  onChange={(event) => setReimbursementForm((prev) => ({ ...prev, date: event.target.value }))}
                />
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Submit Claim
                </Button>
                <Input
                  className="md:col-span-4"
                  placeholder="Description"
                  value={reimbursementForm.description}
                  onChange={(event) => setReimbursementForm((prev) => ({ ...prev, description: event.target.value }))}
                />
              </form>

              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employeeReimbursements.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.type}</TableCell>
                        <TableCell>{item.amount.toLocaleString()}</TableCell>
                        <TableCell>{item.date}</TableCell>
                        <TableCell>
                          <Badge variant={item.status === 'approved' ? 'default' : 'secondary'}>{item.status}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Payroll = () => {
  const { isEmployee, user } = useAuth();
  if (!isEmployee) {
    return <PayrollAdmin />;
  }
  return <EmployeePayrollView user={user} />;
};

export default Payroll;
